'use client';

import { useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { HttpTypes } from '@medusajs/types';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import CountrySelect from '@modules/layout/components/country-select';
import { useToggleState } from '@medusajs/ui';
import { FUN_STORE_NAV } from './fun-store-nav-config';

interface FunStoreMobileMenuProps {
  regions: HttpTypes.StoreRegion[] | null;
  isOpen: boolean;
  onClose: () => void;
}

export function FunStoreMobileMenu({ regions, isOpen, onClose }: FunStoreMobileMenuProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedSubSection, setExpandedSubSection] = useState<string | null>(null);
  const toggleState = useToggleState();

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
    setExpandedSubSection(null);
  };

  const toggleSubSection = (section: string) => {
    setExpandedSubSection(expandedSubSection === section ? null : section);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Menu Panel */}
      <div className="fixed top-0 left-0 h-full w-[85%] max-w-sm bg-white z-50 shadow-2xl overflow-y-auto lg:hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold tracking-wider uppercase">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="p-6">
          <ul className="space-y-1">
            <li>
              <LocalizedClientLink
                href="/"
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                Home
              </LocalizedClientLink>
            </li>

            {/* Mega Menus (Cat, Dog) */}
            {FUN_STORE_NAV.megaMenus.map((menu) => (
              <li key={menu.handle}>
                <button
                  onClick={() => toggleSection(menu.handle)}
                  className="flex items-center justify-between w-full py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                >
                  {menu.label}
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expandedSection === menu.handle ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedSection === menu.handle && (
                  <ul className="pl-4 mt-2 space-y-1 border-l-2 border-gray-200">
                    <li>
                      <LocalizedClientLink
                        href={`/categories/${menu.handle}`}
                        className="block py-2 text-sm font-semibold text-[var(--primary)] hover:underline transition-colors"
                        onClick={onClose}
                      >
                        All {menu.label}
                      </LocalizedClientLink>
                    </li>
                    {menu.groups.map((group) => (
                      <li key={group.label}>
                        <button
                          onClick={() => toggleSubSection(`${menu.handle}-${group.label}`)}
                          className="flex items-center justify-between w-full py-2 text-sm text-gray-600 font-semibold hover:text-[var(--primary)] transition-colors"
                        >
                          {group.label}
                          <ChevronDown
                            size={14}
                            className={`transition-transform ${expandedSubSection === `${menu.handle}-${group.label}` ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {expandedSubSection === `${menu.handle}-${group.label}` && (
                          <ul className="ml-3 mt-1 space-y-1 border-l border-gray-100">
                            {group.handle && (
                              <li>
                                <LocalizedClientLink
                                  href={`/categories/${group.handle}`}
                                  className="block py-1 pl-3 text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                  onClick={onClose}
                                >
                                  All {group.label}
                                </LocalizedClientLink>
                              </li>
                            )}
                            {group.items.map((item) => (
                              <li key={item.handle}>
                                <LocalizedClientLink
                                  href={`/categories/${item.handle}`}
                                  className="block py-1 pl-3 text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                  onClick={onClose}
                                >
                                  {item.label}
                                </LocalizedClientLink>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}

            {/* Dropdowns (Small Animals) */}
            {FUN_STORE_NAV.dropdowns.map((dropdown) => (
              <li key={dropdown.handle}>
                <button
                  onClick={() => toggleSection(dropdown.handle)}
                  className="flex items-center justify-between w-full py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                >
                  {dropdown.label}
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expandedSection === dropdown.handle ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedSection === dropdown.handle && (
                  <ul className="pl-4 mt-2 space-y-1 border-l-2 border-gray-200">
                    <li>
                      <LocalizedClientLink
                        href={`/categories/${dropdown.handle}`}
                        className="block py-2 text-sm font-semibold text-[var(--primary)] hover:underline transition-colors"
                        onClick={onClose}
                      >
                        All {dropdown.label}
                      </LocalizedClientLink>
                    </li>
                    {dropdown.items.map((item) => (
                      <li key={item.handle}>
                        <LocalizedClientLink
                          href={`/categories/${item.handle}`}
                          className="block py-2 text-sm text-gray-600 hover:text-[var(--primary)] transition-colors"
                          onClick={onClose}
                        >
                          {item.label}
                        </LocalizedClientLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}

            {/* Simple Links (Brands) */}
            {FUN_STORE_NAV.links.map((link) => (
              <li key={link.href}>
                <LocalizedClientLink
                  href={link.href}
                  className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                  onClick={onClose}
                >
                  {link.label}
                </LocalizedClientLink>
              </li>
            ))}

            <li>
              <LocalizedClientLink
                href="/store"
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                Shop All
              </LocalizedClientLink>
            </li>

            <li>
              <LocalizedClientLink
                href="/search"
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                Search
              </LocalizedClientLink>
            </li>

            <li>
              <LocalizedClientLink
                href="/account"
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                Account
              </LocalizedClientLink>
            </li>
          </ul>
        </nav>

        {/* Country/Region Selector */}
        {regions && (
          <div className="p-6 border-t border-gray-200">
            <CountrySelect toggleState={toggleState} regions={regions} />
          </div>
        )}
      </div>
    </>
  );
}
